import { certificateTranslations } from './translations/certificates';
import { educationTranslations } from './translations/education';
import { skillTranslations } from './translations/skills';
import { workHistoryTranslations } from './translations/workHistory';
import { projectHistoryTranslations } from './translations/projectHistory';
import { settingsTranslations } from './translations/settings';
import { tabsTranslations } from './translations/tabs';
import { expertViewTranslations } from './translations/ExpertView';
import { languagesTranslations } from './translations/languages';
import { publicationsTranslations } from './translations/publications';
import { documentTranslations } from './translations/documents';
import { projectsTranslations } from './translations/projects';
import { salesTranslations } from './translations/sales';
import { timeEntriesTranslations } from './translations/timeEntries';
import { projectReferencesTranslations } from './translations/projectReferences';
import { jiraTranslations } from './translations/jira';
import { googleCalendarTranslations } from './translations/googleCalendar';
import { calendarTranslations } from './translations/calendar';
import { customersTranslations } from './translations/customers';
import { invoicesTranslations } from './translations/invoices';
import { projectManagementTranslations } from './translations/projectManagement';
import { salaryTranslations } from './translations/salary';

// Define types for individual translation objects
type CertificateTranslationKey = keyof typeof certificateTranslations;
type EducationTranslationKey = keyof typeof educationTranslations;
type SkillTranslationKey = keyof typeof skillTranslations;
type WorkHistoryTranslationKey = keyof typeof workHistoryTranslations;
type ProjectHistoryTranslationKey = keyof typeof projectHistoryTranslations;
type SettingsTranslationKey = keyof typeof settingsTranslations;
type TabsTranslationKey = keyof typeof tabsTranslations;
type ExpertViewTranslationKey = keyof typeof expertViewTranslations;
type LanguagesTranslationKey = keyof typeof languagesTranslations;
type PublicationsTranslationKey = keyof typeof publicationsTranslations;
type DocumentTranslationKey = keyof typeof documentTranslations;
type ProjectsTranslationKey = keyof typeof projectsTranslations;
type SalesTranslationKey = keyof typeof salesTranslations;
type TimeEntriesTranslationKey = keyof typeof timeEntriesTranslations;
type ProjectReferencesTranslationKey = keyof typeof projectReferencesTranslations;
type JiraTranslationKey = keyof typeof jiraTranslations;
type GoogleCalendarTranslationKey = keyof typeof googleCalendarTranslations;
type CalendarTranslationKey = keyof typeof calendarTranslations;
type CustomersTranslationKey = keyof typeof customersTranslations;
type InvoicesTranslationKey = keyof typeof invoicesTranslations;
type ProjectManagementTranslationKey = keyof typeof projectManagementTranslations;
type SalaryTranslationKey = keyof typeof salaryTranslations;
// Direct keys from main translations object
const mainTranslations = {
  // Header translations
  logout: { en: "Logout", fi: "Kirjaudu ulos" },
  people: { en: "People", fi: "Ihmiset" },
  myProfile: { en: "My Profile", fi: "Oma profiili" },

  // New header navigation translations
  "header.timeEntry": { en: "Time Entries", fi: "Tuntikirjaus" },
  "header.hr": { en: "HR", fi: "HR" },
  "header.employees": { en: "Employees", fi: "Työntekijät" },
  "header.timeApproval": { en: "Time Approval", fi: "Tuntien hyväksyntä" },
  "header.customers": { en: "Customers", fi: "Asiakkaat" },
  "header.projectReferences": { en: "References", fi: "Referenssit" },
  "header.experts": { en: "Experts", fi: "Asiantuntijat" },
  "header.resourcing": { en: "Resourcing", fi: "Resursointi" },
  "header.hanselReporting": { en: "Hansel Reporting", fi: "Hansel-raportointi" },
  "header.cv": { en: "CV", fi: "CV" },
  "header.maintenance": { en: "Maintenance", fi: "Ylläpito" },

  // Admin view translations
  expertUsers: { en: "Expert Users", fi: "Asiantuntijat" },
  generateCv: { en: "Generate CV", fi: "Luo CV" },
  generating: { en: "Generating...", fi: "Luodaan..." },
  cvGeneratedSuccess: { en: "CV has been successfully generated", fi: "CV luotu onnistuneesti" },
  attachFile: { en: "Attach file (Text, PDF, Excel, Word)", fi: "Liitä tiedosto (Teksti, PDF, Excel, Word)"},
  attached: { en: "Attached", fi: "Liitetty"},

  // Main view translations
  userManagement: { en: "User Management", fi: "Käyttäjähallinta" },
  changeRole: { en: "Change role", fi: "Vaihda rooli" },

  // Role translations
  "roles.expert": { en: "Expert", fi: "Asiantuntija" },
  "roles.people": { en: "People", fi: "Henkilöt" },
  "roles.projects": { en: "Projects", fi: "Projektit" },
  "roles.sales": { en: "Sales", fi: "Myynti" },
  "roles.admin": { en: "Settings (Admin)", fi: "Asetukset (Admin)" },
  "roles.timeEntries": { en: "Time Entries", fi: "Tuntikirjaukset"},
  "roles.projectLead": { en: "Project Lead", fi: "Projektipäällikkö"},
  "roles.projectReferences": { en: "Project Portfolio", fi: "Projektisalkku"},

  // Common translations
  loading: { en: "Loading...", fi: "Ladataan..." },
  error: { en: "Error", fi: "Virhe" },
  success: { en: "Success", fi: "Onnistui" },
  cancel: { en: "Cancel", fi: "Peruuta" },
  save: { en: "Save", fi: "Tallenna" },
  add: { en: "Add", fi: "Lisää" },
  actions: { en: "Actions", fi: "Toiminnot" },
  saving: { en: "Saving...", fi: "Tallennetaan..." },

  // Search translations
  askAnything: { en: "Ask anything", fi: "Kysy mitä tahansa" },
  search: { en: "Looking for someone?", fi: "Etsitkö jotakuta?" },
  searchComplete: { en: "Search Complete", fi: "Haku Valmis" },
  results: { en: "Results", fi: "Tulosta" },
  result: { en: "Result", fi: "Tulos" },
  userId: { en: "ID", fi: "ID" },
  explanation: { en: "Explanation", fi: "Selitys" },
  bestMatches: { en: "Best Matches", fi: "Parhaat osumat" },
  otherResults: { en: "Other Results", fi: "Muut tulokset" },
  resetChat: { en: "Reset chat", fi:  "Tyhjennä keskustelu" },
  clearSearch: { en: "Clear search", fi: "Tyhjennä haku" },
  processing: { en: "Processing...", fi: "Käsitellään..." },
  botError: { en: "An error occurred while processing your request. Please try again later.", fi: "Käsittelyssä tapahtui virhe. Yritä uudelleen myöhemmin." },

  // CV upload translations
  cvUploadSuccess: { en: "CV uploaded successfully", fi: "CV ladattu onnistuneesti" },
  saveCV: { en: "Save CV", fi: "Tallenna CV" },

  // Loading states
  loadingCvText: { en: "Loading CV text...", fi: "Ladataan CV-tekstiä..." },
  parsingCv: { en: "Parsing CV...", fi: "Jäsennetään ansioluetteloa..." },
  pleaseWait: { en: "Please wait while we process your document", fi: "Odota hetki, kun käsittelemme asiakirjaasi" },

  // Career Goals related translations
  careerGoals: {
    en: 'Career Goals',
    fi: 'Uratavoitteet'
  },
  tellCareerGoals: {
    en: 'Write about your career goals',
    fi: 'Kerro uratavoitteistasi'
  },
  careerGoalsExplanation: {
    en: 'Where do you want to develop, what projects or roles do you hope to get into, or is there a technology that particularly interests you? You can write in a free form - and update the description at any time.',
    fi: 'Missä haluat kehittyä, millaisiin projekteihin tai rooleihin toivot pääseväsi tai onko jokin teknologia, joka erityisesti kiinnostaa? Voit kirjoittaa vapaamuotoisesti - ja päivittää kuvausta milloin tahansa.'
  },
  enterYourCareerGoals: {
    en: 'Enter your career goals...',
    fi: 'Syötä uratavoitteesi...'
  },
  noCareerGoalsYet: {
    en: 'Add your career goals here',
    fi: 'Lisää uratavoitteesi tähän'
  },
  careerGoalsUpdated: {
    en: 'Career goals updated successfully',
    fi: 'Uratavoitteet päivitetty onnistuneesti'
  },

  // Introduction translations
  introductionSaved: {
    en: 'Introduction saved successfully',
    fi: 'Esittely tallennettu onnistuneesti'
  },

  // Title translations
  titleSaved: {
    en: 'Job title saved successfully',
    fi: 'Tehtävänimike tallennettu onnistuneesti'
  },

  // Multi-select deletion translations
  selectAll: {
    en: 'Select All',
    fi: 'Valitse kaikki',
  },
  unselectAll: {
    en: 'Unselect All',
    fi: 'Poista kaikki valinnat',
  },
  deleteSelected: {
    en: 'Delete Selected',
    fi: 'Poista valitut',
  },
  confirmDeleteSelected: {
    en: 'Confirm Deletion',
    fi: 'Vahvista poistaminen',
  },
  deleteSelectedEntriesConfirmation: {
    en: 'Are you sure you want to delete the selected entries? This action cannot be undone.',
    fi: 'Haluatko varmasti poistaa valitut kohteet? Tätä toimintoa ei voi kumota.',
  },
  selectedEntriesDeletedSuccessfully: {
    en: 'Selected entries deleted successfully',
    fi: 'Valitut kohteet poistettu onnistuneesti',
  },

  // Re-embed confirmation translations
  confirmReEmbed: {
    en: 'Confirm Re-embed',
    fi: 'Vahvista uudelleenupotus'
  },
  reEmbedAllConfirmation: {
    en: 'You are about to re-embed every entry in the Milvus database using model:',
    fi: 'Olet upottamassa uudelleen kaikki merkinnät Milvus-tietokannassa käyttäen mallia:'
  },

  // Introduction tab
  introduction: {
    en: 'Introduction',
    fi: 'Esittely'
  },

  // Current projects translations
  ongoing: {
    en: 'Ongoing',
    fi: 'Käynnissä'
  },
  showAllProjects: {
    en: 'Show all projects',
    fi: 'Näytä kaikki projektit'
  },
  noProjects: {
    en: 'No projects',
    fi: 'Ei projekteja'
  },

  startDateRequired: {
    en: 'Start date is required',
    fi: 'Aloituspäivämäärä on pakollinen'
  },

  showAll: {
    en: 'Show all',
    fi: 'Näytä kaikki'
  }
};

type MainTranslationKey = keyof typeof mainTranslations;

// Create a union type of all translation keys
export type TranslationKey =
  | CertificateTranslationKey
  | EducationTranslationKey
  | SkillTranslationKey
  | WorkHistoryTranslationKey
  | ProjectHistoryTranslationKey
  | SettingsTranslationKey
  | TabsTranslationKey
  | ExpertViewTranslationKey
  | LanguagesTranslationKey
  | PublicationsTranslationKey
  | MainTranslationKey
  | DocumentTranslationKey
  | ProjectsTranslationKey
  | SalesTranslationKey
  | TimeEntriesTranslationKey
  | ProjectReferencesTranslationKey
  | JiraTranslationKey
  | GoogleCalendarTranslationKey
  | CalendarTranslationKey
  | CustomersTranslationKey
  | InvoicesTranslationKey
  | ProjectManagementTranslationKey
  | SalaryTranslationKey;

export const translations = {
  ...certificateTranslations,
  ...educationTranslations,
  ...skillTranslations,
  ...workHistoryTranslations,
  ...projectHistoryTranslations,
  ...settingsTranslations,
  ...tabsTranslations,
  ...expertViewTranslations,
  ...languagesTranslations,
  ...publicationsTranslations,
  ...mainTranslations,
  ...documentTranslations,
  ...projectsTranslations,
  ...salesTranslations,
  ...timeEntriesTranslations,
  ...projectReferencesTranslations,
  ...jiraTranslations,
  ...googleCalendarTranslations,
  ...calendarTranslations,
  ...customersTranslations,
  ...invoicesTranslations,
  ...projectManagementTranslations,
  ...salaryTranslations
};

export type Language = 'en' | 'fi';
